import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LayoutService } from './service/app.layout.service';
import { NavMenuService, NavMenuItem, NavRailSection, navColorFromBg } from './nav-menu.service';
import { NavigationHistoryService } from '../../core/services/navigation-history.service';

@Component({
  selector: 'app-nav-panel',
  templateUrl: './app.nav-panel.component.html',
  styleUrls: ['./app.nav-panel.component.scss'],
})
export class AppNavPanelComponent implements OnInit, OnDestroy {
  section: NavRailSection | null = null;
  shortcuts: NavMenuItem[] = [];
  readonly navColor = navColorFromBg;
  private activeShortcut: NavMenuItem | null = null;
  private subs: Subscription[] = [];

  constructor(
    public layoutService: LayoutService,
    private navMenu: NavMenuService,
    private router: Router,
    private navigationHistoryService: NavigationHistoryService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      combineLatest([
        this.layoutService.activeRailId$,
        this.layoutService.previewRailId$,
        this.layoutService.detailPanelOpen$,
        this.navMenu.sections$,
      ]).subscribe(([activeId, previewId]) => {
        const displayId = previewId ?? activeId;
        this.section = this.navMenu.getSectionById(displayId);
        this.shortcuts = this.section?.items || [];
        this.refreshActiveShortcut();
      }),
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => this.refreshActiveShortcut())
    );
  }

  close(): void {
    // Flyout close: just dismiss preview
    if (
      this.layoutService.getPreviewRailId() &&
      !this.layoutService.detailPanelPinned
    ) {
      this.layoutService.clearPreview();
      return;
    }
    this.layoutService.clearPreview();
    this.layoutService.detailPanelPinned = false;
    this.layoutService.setDetailPanelOpen(false, true);
  }

  onShortcutClick(item: NavMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!item?.routerLink?.length) return;
    try {
      item.command?.();
    } catch {
      // ignore command errors so navigation still happens
    }
    this.navigationHistoryService.removeAllValues();
    this.router.navigate(item.routerLink, {
      queryParams: item.queryParams || {},
    });
  }

  isShortcutActive(item: NavMenuItem): boolean {
    return this.activeShortcut === item;
  }

  private refreshActiveShortcut(): void {
    this.activeShortcut = this.findBestMatchingShortcut(this.shortcuts);
  }

  /** Longest path among shortcuts that matches the current URL. */
  private findBestMatchingShortcut(items: NavMenuItem[]): NavMenuItem | null {
    const url = (this.router.url || '').split('?')[0];
    let best: NavMenuItem | null = null;
    let bestLen = -1;

    for (const item of items) {
      if (!item.routerLink?.length) continue;
      const path = '/' + item.routerLink.map(s => String(s).replace(/^\//, '')).join('/');
      const normalized = path.replace(/\/+/g, '/');
      if (url === normalized || url.startsWith(normalized + '/')) {
        if (normalized.length > bestLen) {
          best = item;
          bestLen = normalized.length;
        }
      }
    }
    return best;
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
